import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { useAppState } from "contexts/UserContext";
import { useAuth } from "contexts/AuthContext";
import AppProvider from "contexts/AppProvider";
import { useNotifications } from "hooks/useNotifications";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import OfflineIndicator from "components/OfflineIndicator";
import "../components/i18n";

import { CheckInAdd } from "features/check-in/CheckInAdd/CheckInAdd";

// Features

import DemoTrends from "features/demo/DemoTrends";
import DemoTeamInvite from "../features/demo/DemoTeamInvite";
import TeamDemoLogin from "../features/demo/TeamDemoLogin";
import TeamDemo from "../features/demo/TeamDemo";
import TeamDemoNextSteps from "../features/demo/TeamDemoNextSteps";
import TeamDemoUpgrade from "../features/demo/TeamDemoUpgrade";

import Intro from "features/intro/Intro";
import Intro3 from "../features/intro/Intro3";
import Intro5 from "../features/intro/Intro5";
import Intro2 from "../features/intro/Intro2";

import Login from "@/features/login/Login";
import Startup from "features/startup/Startup";
import Settings from "features/settings/components/Settings";
import { CheckInPage } from "features/check-in/CheckInPage";
import Trends from "features/trends/components/Trends";
import Home from "../features/home";
import CausesOverviewPage from "features/causes/components/CausesOverviewPage";
import TeamsOverview from "features/teams/teams/TeamsOverview";
import SettingsTeamOverview from "features/teams/components/SettingsTeamOverview";
import AuthFinish from "features/authentication/components/AuthFinish";
import Privacy from "features/privacy/Privacy";
import Terms from "features/terms/Terms";
import Hello from "features/hello/Hello";
import Support from "features/support/Support.js";
import Info from "features/support/Info.js";
import Story from "features/story/Story";
import { Reports } from "../features/reports/Reports";

import CheckInView from "../features/check-in/CheckInView/CheckInView";
import { ThemeSettings } from "features/theme/ThemeSettings";
import { NotificationSettings } from "features/notifications/NotificationSettings";
import VaulDrawer from "./Vaul";
import SubscriptionManager from "../features/subscribe/SubscriptionManager";
import "../styles.css";
import WeeklyOverviewCopy from "../features/weeklyOverview/components/WeeklyOverViewCopy";
import TeamWeeklyOverViewCopy from "../features/weeklyOverview/components/TeamWeeklyOverViewCopy";
import JoinWithCode from "../features/join-team/JoinWithCode";
import { TeamDetails } from "../features/teams/TeamDetails/TeamDetails";
import { NoSubscription } from "../features/no-subscription/NoSubscription";
import JoinTeam from "../features/join-team/JoinTeam";
import AccountOverview from "../features/account/AccountOverview";

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoading } = useAppState();
  const { userId, isAuthLoading, accountOrganizationId, isAccountLoading } =
    useAuth();

  if (isLoading || isAuthLoading || (userId && isAccountLoading)) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: "20px",
          backgroundColor: "#ffffff",
          color: "#333",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "60px",
            height: "60px",
            marginBottom: "24px",
          }}
        >
          {/* Outer ring */}
          <div
            style={{
              position: "absolute",
              width: "60px",
              height: "60px",
              border: "4px solid #f0f0f0",
              borderRadius: "50%",
              borderTop: "4px solid #007bff",
              animation: "spin 1.2s linear infinite",
            }}
          ></div>
          {/* Inner ring */}
          <div
            style={{
              position: "absolute",
              top: "8px",
              left: "8px",
              width: "44px",
              height: "44px",
              border: "3px solid #f8f9fa",
              borderRadius: "50%",
              borderTop: "3px solid #6c757d",
              animation: "spin 0.8s linear infinite reverse",
            }}
          ></div>
          {/* Center dot */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "8px",
              height: "8px",
              backgroundColor: "#007bff",
              borderRadius: "50%",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          ></div>
        </div>
        <div
          style={{
            color: "#333",
            fontSize: "18px",
            fontWeight: "500",
            marginBottom: "8px",
            textAlign: "center",
          }}
        >
          Loading... ?
        </div>
        <div
          style={{
            color: "#666",
            fontSize: "14px",
            textAlign: "center",
          }}
        ></div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.4; transform: translate(-50%, -50%) scale(0.8); }
            50% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
          }
        `}</style>
      </div>
    );
  }

  if (!userId) {
    return <Navigate to="/app/login" replace />;
  }

  /*
  if (!isAccountLoading &&!accountOrganizationId) {
    return <Navigate to="/app/startup" replace />;
  }

   */

  return <>{children}</>;
};

const AuthenticatedTrendsEntry: React.FC = () => {
  return <Trends />;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { userId } = useAuth();
  const networkStatus = useNetworkStatus() as {
    isOnline: boolean;
    isOffline: boolean;
    isSlowConnection: boolean;
    connectionType: string;
  };
  const isOnline = networkStatus.isOnline;

  // Note: Offline handling is now managed by the React Native wrapper
  // No need for separate offline fallback here

  return (
    <>
      <OfflineIndicator className="fixed top-4 right-4 z-50 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg" />

      <Routes>
        <Route path="/" element={<Hello />} />
        <Route path="/app/login" element={<Login />} />
        <Route path="/app/startup" element={<Startup />} />
        <Route
          path="/join-code"
          element={<Navigate to="/app/join-code" replace />}
        />
        <Route path="/app/intro" element={<Intro onComplete={undefined} />} />
        <Route path="/app/intro2" element={<Intro2 />} />
        <Route path="/app/intro3" element={<Intro3 />} />
        <Route path="/app/intro5" element={<Intro5 />} />
        <Route path="/app/support" element={<Support />} />
        <Route path="/app/info" element={<Info />} />
        <Route path="/app/auth/finish" element={<AuthFinish />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route
          path="/premium"
          element={
            <ProtectedRoute>
              <SubscriptionManager />
            </ProtectedRoute>
          }
        />
        {
          <Route
            path="/app/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
        }
        <Route
          path="/app/checkin"
          element={
            <ProtectedRoute>
              <CheckInPage />
            </ProtectedRoute>
          }
        >
          <Route path="view" element={<CheckInView />} />
          <Route path="add" element={<CheckInAdd />} />
        </Route>
        {/* Protected Routes */}
        <Route
          path="/app/story"
          element={
            <ProtectedRoute>
              <Story />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/account"
          element={
            <ProtectedRoute>
              <AccountOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/weekly-review"
          element={
            <ProtectedRoute>
              <WeeklyOverviewCopy />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/premium"
          element={
            <ProtectedRoute>
              <SubscriptionManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/causessettings"
          element={
            <ProtectedRoute>
              <CausesOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/trends"
          element={
            <ProtectedRoute>
              <AuthenticatedTrendsEntry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/demo-trends"
          element={
            <ProtectedRoute>
              <DemoTrends />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/demo-team-invite"
          element={
            <ProtectedRoute>
              <DemoTeamInvite />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/theme"
          element={
            <ProtectedRoute>
              <ThemeSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/notifications"
          element={
            <ProtectedRoute>
              <NotificationSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/demo-team"
          element={
            <ProtectedRoute>
              <TeamDemo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/demo-team/next"
          element={
            <ProtectedRoute>
              <TeamDemoNextSteps />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/demo-team/upgrade"
          element={
            <ProtectedRoute>
              <TeamDemoUpgrade />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/upgrade"
          element={
            <ProtectedRoute>
              <TeamDemoUpgrade />
            </ProtectedRoute>
          }
        />
        <Route path="/app/demo-team/login" element={<TeamDemoLogin />} />
        <Route
          path="/app/demo-team/join-code"
          element={
            <ProtectedRoute>
              <JoinWithCode />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/demo-team/weekly"
          element={
            <ProtectedRoute>
              <TeamWeeklyOverViewCopy />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/teammembers"
          element={
            <ProtectedRoute>
              <TeamsOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/teamdetails"
          element={
            <ProtectedRoute>
              <TeamDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/settings/teams"
          element={
            <ProtectedRoute>
              <TeamsOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/join-team/"
          element={
            <ProtectedRoute>
              <JoinTeam />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/settings/teams/team-overview/:teamKey"
          element={
            <ProtectedRoute>
              <SettingsTeamOverview />
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/vaul"
          element={
            <ProtectedRoute>
              <VaulDrawer />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/app/reports"
          element={
            <ProtectedRoute>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/checkin"
          element={
            <ProtectedRoute>
              <CheckInPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/nosubscription"
          element={
            <ProtectedRoute>
              <NoSubscription />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  // Initialize push notifications
  useNotifications();

  // WebView message communication setup
  useEffect(() => {
    // Listen for responses from wrapper
    const handleMessage = (event: MessageEvent) => {
      try {
        if (!event.data || typeof event.data !== "string") {
          console.log("📥 Received non-string data:", event.data);
          return;
        }
        const response = JSON.parse(event.data);
        //console.log('📥 Received from wrapper:', response);

        // Handle response based on response.action
        if (response.type === "RESPONSE") {
          // Trigger a custom event with the response so components can listen for it
          window.dispatchEvent(
            new CustomEvent("wrapperResponse", { detail: response })
          );
        }
      } catch (error) {
        console.warn(
          "Failed to parse message from wrapper. Raw data:",
          event.data,
          "Error:",
          error
        );
      }
    };

    // Set up message listener
    window.addEventListener("message", handleMessage);

    // Make sendMessage globally available
    (window as any).sendMessageToWrapper = (action: string, data: any = {}) => {
      const requestId = Date.now().toString();
      const message = {
        type: "REQUEST",
        action,
        requestId,
        data,
      };

      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
        console.log("📤 Sent to wrapper:", message);
      } else {
        console.warn("⚠️ Not running in app wrapper - message not sent");
      }
    };

    // Cleanup
    return () => {
      window.removeEventListener("message", handleMessage);
      delete (window as any).sendMessageToWrapper;
    };
  }, []);

  return (
    <Router>
      <AppProvider>
        <div className="App">
          {/* Main App Routes */}
          <AppRoutes />
        </div>
      </AppProvider>
    </Router>
  );
};

export default App;
